import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl, generateArticleSchema } from '@/utils/seoUtils';
import { blogPosts } from '@/data/blogPosts';
import { getArticleBySlug, getRelatedArticlesByTopic, getRelatedArticlesByIndustry, articleKeywords } from '@/data/blog';
import EnhancedPageTransition from '@/components/EnhancedPageTransition';
import { motion } from 'framer-motion';
import BlogPostContent from '@/components/BlogPostContent';

const BlogPost = () => {
  const { slug, lang } = useParams<{ slug: string; lang: string }>();
  const navigate = useNavigate();
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const [imageError, setImageError] = useState(false);
  const [hoveredArticleId, setHoveredArticleId] = useState<number | null>(null);
  
  const postFromData = blogPosts.find(post => post.slug === slug);
  const articleContent = slug ? getArticleBySlug(slug) : null;
  
  // Get related articles for this post
  const relatedByTopic = slug ? getRelatedArticlesByTopic(slug, 2) : [];
  const relatedByIndustry = slug ? getRelatedArticlesByIndustry(slug, 2) : [];
  
  // Combine and remove duplicates
  const allRelatedArticles = [...relatedByTopic, ...relatedByIndustry];
  const uniqueRelatedArticles = allRelatedArticles.filter((article, index, self) =>
    index === self.findIndex((a) => a.slug === article.slug)
  ).slice(0, 3); // Limit to 3 related articles
  
  // Select a fallback image based on article topic
  const getFallbackImage = () => {
    // Choose a fallback image based on the article's slug or topic
    if (slug?.includes('video') || slug?.includes('produkt')) {
      return "https://images.unsplash.com/photo-1605810230434-7631ac76ec81?q=80&w=1200&auto=format&fit=crop";
    } else if (slug?.includes('social') || slug?.includes('marketing')) {
      return "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?q=80&w=1200&auto=format&fit=crop";
    } else if (slug?.includes('ki') || slug?.includes('ai')) {
      return "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=1200&auto=format&fit=crop";
    }
    // Default fallback
    return "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1200&auto=format&fit=crop";
  };
  
  // Default fallback image if no image is available or on error
  const fallbackImage = getFallbackImage();
  
  useEffect(() => {
    if (!postFromData && !articleContent) {
      navigate(`/${currentLanguage}/blog`);
      return;
    }
    
    if (articleContent && articleContent.meta[currentLanguage]) {
      const canonicalUrl = getCanonicalUrl(`blog/${slug}`, currentLanguage);
      
      // Get targeted keywords for this article
      const targetKeywords = articleKeywords[slug as keyof typeof articleKeywords] || [];
      
      // Enhanced keywords with more specific terms related to the article topic
      const metaKeywords = `KI Videoproduktion, AI Storytelling, KI Videos Schweiz, ${articleContent.meta[currentLanguage].title}, ${slug?.replace(/-/g, ' ')}, ${targetKeywords.join(', ')}`;
      
      // Update SEO meta tags with more specific keywords and description
      updateMetaTags({
        title: `${articleContent.meta[currentLanguage].title} | Future Level - AI Video Marketing Schweiz`,
        description: articleContent.meta[currentLanguage].description,
        keywords: metaKeywords,
        canonicalUrl: canonicalUrl,
        language: currentLanguage,
        ogImage: articleContent.image,
        ogType: "article"
      });
      
      // Add enhanced schema.org markup for the article with more specific metadata
      generateArticleSchema(
        articleContent.meta[currentLanguage].title,
        articleContent.meta[currentLanguage].description,
        articleContent.image,
        articleContent.date,
        canonicalUrl,
        currentLanguage,
        metaKeywords
      );
    } else if (postFromData) {
      // For regular blog posts without full content
      const canonicalUrl = getCanonicalUrl(`blog/${slug}`, currentLanguage);
      const metaKeywords = `KI Videoproduktion, AI Video Marketing, Future Level, KI Videos Schweiz, ${postFromData.title[currentLanguage]}, ${slug?.replace(/-/g, ' ')}`;
      
      updateMetaTags({
        title: `${postFromData.title[currentLanguage]} | Future Level - KI Video Agentur`,
        description: postFromData.excerpt[currentLanguage],
        keywords: metaKeywords,
        canonicalUrl: canonicalUrl,
        language: currentLanguage,
        ogImage: postFromData.image,
        ogType: "article"
      });
      
      // Add schema.org markup for basic blog posts too
      generateArticleSchema(
        postFromData.title[currentLanguage],
        postFromData.excerpt[currentLanguage],
        postFromData.image,
        postFromData.date,
        canonicalUrl,
        currentLanguage,
        metaKeywords
      );
    }
  }, [postFromData, articleContent, currentLanguage, navigate, slug, getLocalizedPath]);
  
  if (!postFromData && !articleContent) return null;
  
  const post = articleContent || postFromData;
  const postImage = articleContent ? articleContent.image : postFromData?.image;
  const postTitle = articleContent ? 
    articleContent.meta[currentLanguage].title : 
    postFromData?.title[currentLanguage];
  const postDate = articleContent ? articleContent.date : postFromData?.date;
  
  // Function to handle fallback image if main image fails
  const handleImageError = () => {
    console.error("Image failed to load:", postImage);
    setImageError(true);
  };

  // Animation variants for micro-interactions
  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.7 } }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    hover: { y: -5, transition: { duration: 0.3 } }
  };
  
  return (
    <EnhancedPageTransition variant="fade" duration={0.4}>
      <div className="flex flex-col min-h-screen bg-[#121212]">
        <Header />
        <main className="flex-grow py-16 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]" itemScope itemType="https://schema.org/Article">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.nav 
              className="mb-6" 
              aria-label="Breadcrumb"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <ol className="flex items-center space-x-2 text-sm flex-wrap">
                <li>
                  <Link to={getLocalizedPath('/')} className="text-purple-400 hover:underline">
                    {t.home}
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <Link to={getLocalizedPath('blog')} className="text-purple-400 hover:underline">
                    {t.blog}
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <span className="text-gray-300" itemProp="headline">{postTitle}</span>
                </li>
              </ol>
            </motion.nav>
            
            <div className="max-w-4xl mx-auto">
              {/* Enhanced image with better error handling */}
              <motion.div 
                className="w-full h-72 md:h-96 rounded-xl mb-8 shadow-lg overflow-hidden"
                variants={contentVariants}
                initial="initial"
                animate="animate"
              >
                <img 
                  src={imageError ? fallbackImage : postImage || fallbackImage}
                  alt={postTitle || "Blog post featured image"}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  itemProp="image"
                  onError={handleImageError}
                  loading="eager" // Load this quickly as it's the main content
                  width="800"
                  height="450"
                />
              </motion.div>
              
              <motion.div 
                className="glass-card rounded-xl p-6 md:p-8 shadow-lg mb-6"
                variants={contentVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.2 }}
              >
                <time dateTime={postDate} className="text-sm text-purple-300" itemProp="datePublished">
                  {postDate}
                </time>
                
                {articleContent ? (
                  <BlogPostContent 
                    htmlContent={articleContent.content[currentLanguage]} 
                    languageCode={currentLanguage} 
                  />
                ) : (
                  <>
                    <motion.h1 
                      className="text-3xl md:text-4xl font-bold mt-2 mb-6 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400" 
                      itemProp="headline"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      {postTitle}
                    </motion.h1>
                    
                    <motion.div 
                      className="prose prose-invert max-w-none text-gray-300" 
                      itemProp="articleBody"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.7 }}
                    >
                      <p className="text-lg mb-6">{postFromData?.excerpt[currentLanguage]}</p>
                      
                      <p className="mb-4">
                        {t.blogContentPlaceholder}
                      </p>
                      
                      <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading1}</h2>
                      <p className="mb-4">
                        {t.blogContentPlaceholder}
                      </p>
                      
                      <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading2}</h2>
                      <p className="mb-4">
                        {t.blogContentPlaceholder}
                      </p>
                      
                      <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading3}</h2>
                      <p className="mb-4">
                        {t.blogContentPlaceholder}
                      </p>
                      
                      <div className="mt-12 p-6 bg-[#1A1F2C]/80 rounded-lg border border-purple-500/20">
                        <h3 className="text-xl font-semibold mb-4 text-white">{t.blogCTA}</h3>
                        <Link to={getLocalizedPath('contact')} className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                          {t.contactUs}
                        </Link>
                      </div>
                    </motion.div>
                  </>
                )}
                
                {/* Related Articles Section */}
                {uniqueRelatedArticles.length > 0 && (
                  <motion.div 
                    className="mt-16 border-t border-purple-900/30 pt-8"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    <h3 className="text-2xl font-semibold text-white mb-6 font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400">
                      {currentLanguage === 'de' ? "Verwandte Artikel" :
                       currentLanguage === 'en' ? "Related Articles" :
                       currentLanguage === 'fr' ? "Articles Connexes" :
                       "Articoli Correlati"}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      {uniqueRelatedArticles.map((article, idx) => (
                        <motion.div 
                          key={idx}
                          variants={cardVariants}
                          initial="initial"
                          animate="animate"
                          whileHover="hover"
                          transition={{ delay: 0.8 + (idx * 0.1) }}
                          onHoverStart={() => setHoveredArticleId(idx)}
                          onHoverEnd={() => setHoveredArticleId(null)}
                        >
                          <Link 
                            to={`/${currentLanguage}/blog/${article.slug}`}
                            className="block"
                          >
                            <div className="rounded-lg overflow-hidden bg-[#1A1F2C] hover:bg-[#222a3a] transition-colors border border-purple-900/20 h-full shadow-md hover:shadow-lg hover:shadow-purple-500/10">
                              <div className="relative overflow-hidden h-40">
                                <motion.img 
                                  src={article.image || fallbackImage} 
                                  alt={article.meta[currentLanguage].title}
                                  className="w-full h-full object-cover"
                                  animate={{ 
                                    scale: hoveredArticleId === idx ? 1.05 : 1,
                                  }}
                                  transition={{ duration: 0.4 }}
                                  loading="lazy"
                                  width="300"
                                  height="160"
                                />
                                <motion.div 
                                  className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"
                                  animate={{ opacity: hoveredArticleId === idx ? 0.8 : 0.6 }}
                                />
                              </div>
                              <div className="p-4">
                                <p className="text-xs text-purple-400 mb-1">{article.date}</p>
                                <h4 className="text-white font-medium mb-2 transition-colors">
                                  {article.meta[currentLanguage].title}
                                </h4>
                                <p className="text-gray-400 text-sm line-clamp-2">
                                  {article.meta[currentLanguage].description}
                                </p>
                                <motion.div 
                                  className="flex items-center mt-3 text-xs text-purple-300"
                                  animate={{ x: hoveredArticleId === idx ? 2 : 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {t.readMore}
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </motion.div>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </EnhancedPageTransition>
  );
};

export default BlogPost;
